import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { isValidWorkersCompCode } from '@careerstart/wae-common/schema/models/position-template/subSchemas/workersCompCodeSchema';
import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';

import WaeButton, { BUTTON_VARIANT } from '../../../components/Button';
import SearchableDropdown, {
  SEARCHABLE_DROPDOWN_VARIANT,
} from '../../../components/SearchableDropdown';
import WaeStandardTextField from '../../../components/WaeStandardTextField/WaeStandardTextField';
import { BLACK, FEED_BACK, PRIMARY_PURPLE } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import formatAddress from '../../../utils/formatAddress';
import SectionCard from '../../positionTemplate/components/SectionCard';
import locationOptions from '../api/locationOptions';
import { textFieldSx } from '../components/formFieldStyles';
import RequiredMark from '../components/RequiredMark';
import { getSectionAnchorId, SECTION_IDS } from '../constants';

const GRID_COLUMNS = 'minmax(200px, 1fr) 120px minmax(160px, 220px) 40px';
// Partial input toward a code isValidWorkersCompCode accepts: 3-6 digits, ####-##, or 3-6 digits plus a letter.
const COMP_CODE_INPUT_PATTERN = /^(?:\d{0,6}|\d{3,6}[A-Z]|\d{4}-\d{0,2})$/;

const columnLabelSx = {
  color: BLACK[60],
  fontFamily: PRIMARY_FONT[700],
  fontSize: '11px',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const locationShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  address: PropTypes.string,
  jurisdiction: PropTypes.string,
  compCode: PropTypes.string,
});

export const toLocationRow = (location) => ({
  id: location?._id,
  name: location?.name || location?.description || '',
  address: formatAddress(location),
  jurisdiction: location?.state || '',
  compCode: '',
});

const getLocationLabel = (option) => option?.name || option?.description || '';

const ProgressChip = ({ complete, total }) => {
  const allComplete = complete === total && total > 0;

  return (
    <Box
      component="span"
      data-testid="locations-progress"
      sx={{
        backgroundColor: allComplete ? FEED_BACK.GREEN_90 : BLACK[0],
        border: `1px solid ${allComplete ? FEED_BACK.GREEN_10 : BLACK[30]}`,
        borderRadius: '999px',
        color: allComplete ? FEED_BACK.GREEN_100 : BLACK[80],
        fontFamily: PRIMARY_FONT[700],
        fontSize: '12px',
        padding: '2px 10px',
      }}
    >
      {LanguageConverterV2('positionTemplate.create.locations.progress', { complete, total })}
    </Box>
  );
};

ProgressChip.propTypes = {
  complete: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
};

const ColumnHeaders = () => (
  <Box
    sx={{
      backgroundColor: BLACK[10],
      columnGap: '16px',
      display: 'grid',
      gridTemplateColumns: GRID_COLUMNS,
      padding: '10px 16px',
    }}
  >
    <Typography sx={columnLabelSx}>
      {LanguageConverterV2('positionTemplate.create.locations.column.location')}
    </Typography>
    <Typography sx={columnLabelSx}>
      {LanguageConverterV2('positionTemplate.create.locations.column.jurisdiction')}
    </Typography>
    <Typography sx={columnLabelSx}>
      {LanguageConverterV2('positionTemplate.create.locations.column.compCode')}
    </Typography>
    <Box />
  </Box>
);

const JurisdictionPill = ({ jurisdiction }) => (
  <Box
    data-testid="location-jurisdiction"
    sx={{
      alignItems: 'center',
      backgroundColor: BLACK[20],
      borderRadius: '999px',
      color: BLACK[80],
      display: 'inline-flex',
      fontFamily: PRIMARY_FONT[700],
      fontSize: '12px',
      gap: '4px',
      padding: '4px 10px',
      width: 'fit-content',
    }}
  >
    {jurisdiction}
    <LockOutlinedIcon aria-hidden sx={{ color: BLACK[60], fontSize: 14 }} />
  </Box>
);

JurisdictionPill.propTypes = { jurisdiction: PropTypes.string };

const LocationRow = ({ location, onCompCodeChange, onDelete }) => {
  const compCodeLabel = LanguageConverterV2('positionTemplate.create.locations.column.compCode');
  const deleteLabel = LanguageConverterV2('positionTemplate.create.locations.delete', {
    name: location.name,
  });

  const handleCompCodeChange = (event) => {
    const nextValue = event.target.value.toUpperCase();
    if (COMP_CODE_INPUT_PATTERN.test(nextValue)) onCompCodeChange(nextValue);
  };

  return (
    <Box
      data-testid="location-row"
      sx={{
        alignItems: 'center',
        borderTop: `1px solid ${BLACK[20]}`,
        columnGap: '16px',
        display: 'grid',
        gridTemplateColumns: GRID_COLUMNS,
        padding: '12px 16px',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}>
          {location.name}
        </Typography>
        {location.address && (
          <Typography
            sx={{
              color: BLACK[70],
              fontFamily: PRIMARY_FONT[400],
              fontSize: '12px',
              marginTop: '2px',
            }}
          >
            {location.address}
          </Typography>
        )}
      </Box>
      <JurisdictionPill jurisdiction={location.jurisdiction} />
      <WaeStandardTextField
        value={location.compCode}
        onChange={handleCompCodeChange}
        InputProps={{
          inputProps: { 'aria-label': compCodeLabel },
          endAdornment: isValidWorkersCompCode(location.compCode) ? (
            <InputAdornment position="end">
              <CheckIcon
                aria-hidden
                data-testid="comp-code-valid"
                sx={{ color: FEED_BACK.GREEN_70, fontSize: 18 }}
              />
            </InputAdornment>
          ) : null,
        }}
        sx={textFieldSx}
      />
      <IconButton
        aria-label={deleteLabel}
        onClick={onDelete}
        size="small"
        sx={{ color: BLACK[60] }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

LocationRow.propTypes = {
  location: locationShape.isRequired,
  onCompCodeChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const FooterNote = () => (
  <Box
    sx={{
      alignItems: 'flex-start',
      backgroundColor: BLACK[10],
      borderTop: `1px solid ${BLACK[20]}`,
      display: 'flex',
      gap: '8px',
      padding: '12px 16px',
    }}
  >
    <InfoOutlinedIcon aria-hidden sx={{ color: BLACK[60], fontSize: 16, marginTop: '1px' }} />
    <Typography
      sx={{
        color: BLACK[70],
        fontFamily: PRIMARY_FONT[400],
        fontSize: '12px',
        fontStyle: 'italic',
      }}
    >
      {LanguageConverterV2('positionTemplate.create.locations.note')}{' '}
      <Box component="span" sx={{ fontFamily: PRIMARY_FONT[700] }}>
        {LanguageConverterV2('positionTemplate.create.locations.noteEmphasis')}
      </Box>
    </Typography>
  </Box>
);

const LocationsSection = ({ values, setField }) => {
  const title = LanguageConverterV2('positionTemplate.create.locations.title');
  const addLabel = LanguageConverterV2('positionTemplate.create.locations.add');
  const searchPlaceholder = LanguageConverterV2(
    'positionTemplate.create.locations.searchPlaceholder'
  );
  const emptyText = LanguageConverterV2('positionTemplate.create.locations.empty');

  const locations = values.locations || [];
  const corporationId = values.corporation?._id || '';
  const [pickerFor, setPickerFor] = useState(null);
  const showPicker = !!corporationId && pickerFor === corporationId;
  const completeCount = locations.filter((location) =>
    isValidWorkersCompCode(location.compCode)
  ).length;

  const addLocation = (event, option) => {
    if (!option?._id || locations.some((location) => location.id === option._id)) return;
    setField('locations', [...locations, toLocationRow(option)]);
    setPickerFor(null);
  };

  const updateCompCode = (id, compCode) =>
    setField(
      'locations',
      locations.map((location) => (location.id === id ? { ...location, compCode } : location))
    );

  const deleteLocation = (id) =>
    setField(
      'locations',
      locations.filter((location) => location.id !== id)
    );

  return (
    <Box
      id={getSectionAnchorId(SECTION_IDS.LOCATIONS)}
      data-testid="locations-section"
      sx={{ scrollMarginTop: '16px' }}
    >
      <SectionCard
        header={
          <>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: '10px' }}>
              <Typography
                sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}
              >
                {title}
                <RequiredMark />
              </Typography>
              {locations.length > 0 && (
                <ProgressChip complete={completeCount} total={locations.length} />
              )}
            </Box>
            <WaeButton
              dataTestId="add-location"
              disabled={!corporationId}
              onClick={() => setPickerFor(showPicker ? null : corporationId)}
              startIcon={<AddIcon sx={{ color: PRIMARY_PURPLE[70] }} />}
              text={addLabel}
              variant={BUTTON_VARIANT.OUTLINED}
            />
          </>
        }
      >
        <Box sx={{ margin: '-8px -16px' }}>
          {showPicker && (
            <Box sx={{ padding: '12px 16px' }}>
              <SearchableDropdown
                value={null}
                onChange={addLocation}
                optionsAPICallback={locationOptions(corporationId)}
                getOptionLabel={getLocationLabel}
                placeholder={searchPlaceholder}
                variant={SEARCHABLE_DROPDOWN_VARIANT.SEARCH}
                excludeIds={locations.map((location) => location.id)}
                testId="position-template-location"
              />
            </Box>
          )}
          <ColumnHeaders />
          {locations.length === 0 ? (
            <Typography
              sx={{
                borderTop: `1px solid ${BLACK[20]}`,
                color: BLACK[60],
                fontFamily: PRIMARY_FONT[400],
                fontSize: '13px',
                padding: '20px 16px',
                textAlign: 'center',
              }}
            >
              {emptyText}
            </Typography>
          ) : (
            locations.map((location) => (
              <LocationRow
                key={location.id}
                location={location}
                onCompCodeChange={(compCode) => updateCompCode(location.id, compCode)}
                onDelete={() => deleteLocation(location.id)}
              />
            ))
          )}
          <FooterNote />
        </Box>
      </SectionCard>
    </Box>
  );
};

LocationsSection.propTypes = {
  values: PropTypes.shape({
    corporation: PropTypes.shape({ _id: PropTypes.string }),
    locations: PropTypes.arrayOf(locationShape),
  }).isRequired,
  setField: PropTypes.func.isRequired,
};

export default LocationsSection;
